import { makeAutoObservable } from "mobx";
import type { RootStore } from "./root.store";
import {
  getVisibleProfileSettingsTabs,
  type IProfileSettingsTab,
  type TProfileSettingsTabKey,
} from "@/lib/permissions/profile-permissions";

export class ProfileSettingsStore {
  private rootStore: RootStore;
  isOpen = false;
  activeTab: TProfileSettingsTabKey = "general";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** The tabs this person can see. The flag predicate is passed rather than
   *  defaulted, because the default fails closed: a flagged tab is invisible
   *  until somebody hands the check a real answer, and this store has one. */
  get tabs(): IProfileSettingsTab[] {
    return getVisibleProfileSettingsTabs((key) =>
      this.rootStore.featureFlags.isEnabled(key, false),
    );
  }

  /** The tab the modal shows: `activeTab` while it is visible, General otherwise.
   *  A deep link or `open(tab)` can name a flagged tab before the flags answer, or
   *  with its flag off; derived here rather than corrected in `open`, so the tab
   *  appears by itself once its flag turns on. */
  get visibleActiveTab(): TProfileSettingsTabKey {
    return this.tabs.some((tab) => tab.key === this.activeTab) ? this.activeTab : "general";
  }

  open(tab: TProfileSettingsTabKey = "general"): void {
    this.activeTab = tab;
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
  }

  setOpen(open: boolean): void {
    this.isOpen = open;
  }

  setActiveTab(tab: TProfileSettingsTabKey): void {
    this.activeTab = tab;
  }
}
